import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Chip,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { BoxedImage } from '../../../components/BoxedImage';
import { DesignResponse, PogBBox, ProductRecognitionCropScanResponse } from '../../../types';
import { ImageZoomModal } from './ImageZoomModal';
import { ProductRecognitionViewer } from './ProductRecognitionViewer';

const ITEM_PREVIEW_SIZE = 120;

const getClassificationColor = (classificationType: string): string => {
  switch (classificationType) {
    case 'product':
      return '#4CAF50'; // Green for products
    case 'carton':
      return '#FF9800'; // Orange for cartons
    default:
      return '#2196F3'; // Blue for unknown/other types
  }
};

interface CropScanItemProps {
  cropScan: ProductRecognitionCropScanResponse & {
    isPending?: boolean;
    shelfColumnLabel?: string;
    orientation?: 'front' | 'not-front';
    pogPreview?: {
      pogBox: PogBBox;
    };
    designs?: DesignResponse[];
    selectedDesignId?: string;
    itemNumber?: string;
  };
  index: number;
  imageUrl: string;
  previewSize?: number;
  onCropScanClick?: (cropScan: ProductRecognitionCropScanResponse) => void;
  onCropViewerClick?: (cropScan: ProductRecognitionCropScanResponse) => void;
  onDelete?: (productCropScanId: number) => void;
  onToggleOrientation?: (productCropScanId: number) => void;
  onSelectDesign?: (productCropScanId: number, designId: string) => void;
  isSelected?: boolean;
}

export const CropScanItem: React.FC<CropScanItemProps> = ({
  cropScan,
  index,
  imageUrl,
  previewSize = ITEM_PREVIEW_SIZE,
  onCropScanClick,
  onCropViewerClick,
  onDelete,
  onToggleOrientation,
  onSelectDesign,
  isSelected,
}) => {
  const [zoomState, setZoomState] = useState<{
    src: string;
    bbox?: [number, number, number, number];
  } | null>(null);
  const [loadedImage, setLoadedImage] = useState<HTMLImageElement | null>(null);
  const [pogZoomSrc, setPogZoomSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!zoomState) {
      setLoadedImage(null);
      return;
    }
    const img = new Image();
    img.onload = () => setLoadedImage(img);
    img.src = zoomState.src;
  }, [zoomState]);

  return (
    <>
      <Paper
        elevation={2}
        sx={{
          p: 1,
          cursor: onCropScanClick ? 'pointer' : 'default',
          backgroundColor: isSelected ? '#FFF3CD' : '#FFFFFF',
          border: isSelected ? '2px solid #FFC107' : '2px solid transparent',
          '&:hover': onCropScanClick
            ? {
                backgroundColor: isSelected ? '#FFE69C' : 'action.hover',
              }
            : {},
        }}
        onClick={() => onCropScanClick?.(cropScan)}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Crop + POG Images */}
          <Box
            sx={{
              minWidth: '70px',
              alignSelf: 'flex-start',
              display: 'flex',
              alignItems: 'center',

              flexDirection: 'row',
              gap: 2,
            }}
          >
            {/* Camera crop */}
            <ProductRecognitionViewer
              imageUrl={imageUrl}
              cropScans={[cropScan]}
              mode="crop"
              focusCropScan={cropScan}
              width={previewSize}
              height={previewSize}
              onClick={() => {
                if (onCropViewerClick) {
                  onCropViewerClick(cropScan);
                } else {
                  setZoomState({
                    src: imageUrl,
                    bbox: cropScan.bbox as [number, number, number, number],
                  });
                }
              }}
              sx={{ cursor: 'zoom-in' }}
              hidePolygons
            />
            {/* POG item image - only when we have a matching POG box */}
            {cropScan.pogPreview &&
              cropScan.pogPreview.pogBox.imageUri &&
              !cropScan.pogPreview.pogBox.hasFallbackImage && (
                <>
                  &rarr;
                  <img
                    src={cropScan.pogPreview.pogBox.imageUri}
                    alt="POG item"
                    style={{
                      maxWidth: previewSize,
                      maxHeight: previewSize,
                      objectFit: 'contain',
                      borderRadius: 4,
                      border: '1px solid',
                      borderColor: 'rgba(0, 0, 0, 0.12)',
                      cursor: 'zoom-in',
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      setPogZoomSrc(cropScan.pogPreview!.pogBox.imageUri!);
                    }}
                  />
                </>
              )}
          </Box>

          {/* Crop Info */}
          <Stack spacing={1} sx={{ flex: 1, width: '100%' }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
              <Typography variant="subtitle2" sx={{ fontSize: '12px' }}>
                # {index + 1}
              </Typography>
              <Chip
                label={cropScan.classification_type}
                size="small"
                sx={{
                  backgroundColor: getClassificationColor(cropScan.classification_type),
                  color: 'white',
                  fontWeight: 500,
                  textTransform: 'capitalize',
                  fontSize: '12px',
                }}
              />
              {cropScan.shelfColumnLabel && (
                <Chip
                  label={cropScan.shelfColumnLabel}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                  }}
                />
              )}
              {cropScan.isPending && (
                <Chip label="Pending" color="warning" size="small" sx={{ fontSize: '12px' }} />
              )}
              {cropScan.isPending && onToggleOrientation && (
                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  control={
                    <Switch
                      size="small"
                      checked={cropScan.orientation !== 'not-front'}
                      onChange={() => onToggleOrientation(cropScan.product_crop_scan_id)}
                    />
                  }
                  label={
                    <Typography sx={{ fontSize: '12px', fontWeight: 600 }}>
                      {cropScan.orientation === 'not-front' ? 'Non-front' : 'Front'}
                    </Typography>
                  }
                  sx={{ ml: 0, mr: 0 }}
                />
              )}
              {!cropScan.isPending && (cropScan.barcode || cropScan.normalized_barcode) && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Complete"
                  color="success"
                  size="small"
                  sx={{ fontSize: '12px' }}
                />
              )}
            </Stack>

            <Box>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '12px',
                  textAlign: 'left',
                }}
              >
                {cropScan.normalized_barcode || cropScan.barcode
                  ? `${cropScan.normalized_barcode || cropScan.barcode} • ${cropScan.product_name}${
                      cropScan.itemNumber ? ` • Item #: ${cropScan.itemNumber}` : ''
                    }`
                  : '\u00A0'}
              </Typography>
            </Box>

            {cropScan.classification_type !== 'carton' &&
              ((cropScan.designs && cropScan.designs.length > 0) || onSelectDesign) && (
                <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {[...(cropScan.designs ?? [])]
                    .sort((a, b) => {
                      const aIntent = Boolean(
                        (a as DesignResponse & { __isIntent?: boolean }).__isIntent
                      );
                      const bIntent = Boolean(
                        (b as DesignResponse & { __isIntent?: boolean }).__isIntent
                      );
                      // Intents render after real designs.
                      if (aIntent !== bIntent) return aIntent ? 1 : -1;
                      // Within real designs, those with front/back images first.
                      const aHasFrontOrBack = Boolean(a.frontImageUrl || a.backImageUrl);
                      const bHasFrontOrBack = Boolean(b.frontImageUrl || b.backImageUrl);
                      if (aHasFrontOrBack !== bHasFrontOrBack) {
                        return aHasFrontOrBack ? -1 : 1;
                      }
                      return 0;
                    })
                    .map((design) => {
                      const isIntent = Boolean(
                        (design as DesignResponse & { __isIntent?: boolean }).__isIntent
                      );
                      const isDesignSelected = cropScan.selectedDesignId === design._id;

                      if (isIntent) {
                        return (
                          <Stack
                            key={design._id}
                            direction="column"
                            spacing={0.5}
                            alignItems="center"
                            onClick={(e) => {
                              if (!onSelectDesign) return;
                              e.stopPropagation();
                              onSelectDesign(cropScan.product_crop_scan_id, design._id);
                            }}
                            sx={{
                              minWidth: 80,
                              border: isDesignSelected
                                ? '2px dashed #1976d2'
                                : '2px dashed rgba(0, 0, 0, 0.3)',
                              borderRadius: 1,
                              p: 0.5,
                              backgroundColor: isDesignSelected ? '#E3F2FD' : 'transparent',
                              cursor: onSelectDesign ? 'pointer' : 'default',
                              '&:hover': onSelectDesign
                                ? { borderColor: isDesignSelected ? '#1976d2' : '#90CAF9' }
                                : {},
                            }}
                          >
                            {(() => {
                              const intentBbox = (
                                design as DesignResponse & {
                                  __intentBbox?: [number, number, number, number];
                                }
                              ).__intentBbox;
                              if (!intentBbox) {
                                return (
                                  <Box
                                    sx={{
                                      width: 80,
                                      height: 80,
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      border: '1px dashed rgba(0, 0, 0, 0.2)',
                                      borderRadius: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="caption"
                                      sx={{ fontSize: '10px', color: 'text.secondary' }}
                                    >
                                      No preview
                                    </Typography>
                                  </Box>
                                );
                              }
                              const [bx, by, bw, bh] = intentBbox;
                              const maxSize = 80;
                              const scale = maxSize / Math.max(bw, bh);
                              const renderW = Math.max(1, Math.round(bw * scale));
                              const renderH = Math.max(1, Math.round(bh * scale));
                              return (
                                <svg
                                  width={renderW}
                                  height={renderH}
                                  viewBox={`${bx} ${by} ${bw} ${bh}`}
                                  preserveAspectRatio="xMidYMid slice"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setZoomState({ src: imageUrl, bbox: intentBbox });
                                  }}
                                  style={{
                                    display: 'block',
                                    borderRadius: 4,
                                    cursor: 'zoom-in',
                                  }}
                                >
                                  <image href={imageUrl} x={0} y={0} />
                                </svg>
                              );
                            })()}
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '11px',
                                fontWeight: 600,
                                color: '#1565C0',
                                textAlign: 'center',
                                maxWidth: 170,
                              }}
                            >
                              {design.productName ? `${design.productName} (new)` : 'New'}
                            </Typography>
                            {design.normalizedBarcode && (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '10px',
                                  color: '#1565C0',
                                  fontFamily: 'monospace',
                                }}
                              >
                                {design.normalizedBarcode}
                              </Typography>
                            )}
                          </Stack>
                        );
                      }

                      const images: Array<{
                        src: string;
                        label: 'Front' | 'Back' | 'Representative';
                      }> = [];
                      if (design.frontImageUrl) {
                        images.push({ src: design.frontImageUrl, label: 'Front' });
                      }
                      if (design.backImageUrl) {
                        images.push({ src: design.backImageUrl, label: 'Back' });
                      }
                      if (images.length === 0 && design.representativeVizUrl) {
                        images.push({ src: design.representativeVizUrl, label: 'Representative' });
                      }
                      if (images.length === 0) return null;
                      return (
                        <Stack
                          key={design._id}
                          direction="column"
                          spacing={0.5}
                          alignItems="center"
                          onClick={(e) => {
                            if (!onSelectDesign) return;
                            e.stopPropagation();
                            onSelectDesign(cropScan.product_crop_scan_id, design._id);
                          }}
                          sx={{
                            border: isDesignSelected
                              ? '2px solid #1976d2'
                              : '2px solid rgba(0, 0, 0, 0.12)',
                            borderRadius: 1,
                            p: 0.5,
                            backgroundColor: isDesignSelected ? '#E3F2FD' : 'transparent',
                            cursor: onSelectDesign ? 'pointer' : 'default',
                            '&:hover': onSelectDesign
                              ? { borderColor: isDesignSelected ? '#1976d2' : '#90CAF9' }
                              : {},
                          }}
                        >
                          <Stack direction="row" spacing={0.5}>
                            {images.map(({ src, label }) => (
                              <Box
                                key={label}
                                sx={{
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                }}
                              >
                                <img
                                  src={src}
                                  alt={`${label} design`}
                                  style={{
                                    maxWidth: 80,
                                    maxHeight: 80,
                                    objectFit: 'contain',
                                    borderRadius: 4,
                                    cursor: 'zoom-in',
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setPogZoomSrc(src);
                                  }}
                                />
                                <Typography
                                  variant="caption"
                                  sx={{ fontSize: '10px', color: 'text.secondary' }}
                                >
                                  {label}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                          {(() => {
                            const pogBarcode = cropScan.normalized_barcode || cropScan.barcode;
                            const displayName =
                              design.productName ||
                              (design.normalizedBarcode &&
                              pogBarcode &&
                              design.normalizedBarcode === pogBarcode
                                ? cropScan.product_name
                                : null);
                            if (!displayName) return null;
                            return (
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  color: 'text.primary',
                                  textAlign: 'center',
                                  maxWidth: 170,
                                }}
                              >
                                {displayName}
                              </Typography>
                            );
                          })()}
                          {design.normalizedBarcode && (
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '10px',
                                color: 'text.secondary',
                                fontFamily: 'monospace',
                              }}
                            >
                              {design.normalizedBarcode}
                            </Typography>
                          )}
                        </Stack>
                      );
                    })}
                  {onSelectDesign && (
                    <Box
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectDesign(cropScan.product_crop_scan_id, 'new');
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: 80,
                        height: 96,
                        border: '2px dashed rgba(0, 0, 0, 0.3)',
                        borderRadius: 1,
                        p: 0.5,
                        backgroundColor: 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          borderColor: '#90CAF9',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontSize: '11px',
                          fontWeight: 600,
                          color: 'text.secondary',
                          textAlign: 'center',
                        }}
                      >
                        + New design
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
          </Stack>

          {/* Delete Button */}
          {(cropScan.barcode || cropScan.normalized_barcode) && cropScan.isPending && onDelete && (
            <div>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(cropScan.product_crop_scan_id);
                }}
                sx={{
                  color: 'error.main',
                  '&:hover': {
                    backgroundColor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          )}
        </Stack>
      </Paper>
      <ImageZoomModal
        isOpen={!!pogZoomSrc}
        imageSrc={pogZoomSrc ?? ''}
        onClose={() => setPogZoomSrc(null)}
      />
      {zoomState && (
        <div
          className="fixed inset-0 bg-black bg-opacity-90 flex justify-center z-50 p-8 overflow-y-auto"
          onClick={() => setZoomState(null)}
        >
          <div className="relative max-w-6xl m-auto" onClick={(e) => e.stopPropagation()}>
            {loadedImage ? (
              <BoxedImage image={loadedImage} crop={zoomState.bbox} cropPadding={20} boxes={[]} />
            ) : (
              <div className="text-white">Loading...</div>
            )}
            <button
              onClick={() => setZoomState(null)}
              className="absolute top-4 right-4 text-white bg-black bg-opacity-50 rounded-full p-2 hover:bg-opacity-75"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      )}
    </>
  );
};
